import NotificationMessage from '@/components/common/NotificationMessage.vue';
import { ButtonVariant } from '@/models/button-variant.enum';
import { PopUpType } from '@/models/pop-up-type.enum';
import { createApp, type App } from 'vue';

export interface Notifier {
  success: (messageBody: string) => void;
  warning: (messageBody: string) => void;
  error: (
    messageBody: string,
    options?: { buttons: [{ text: string; action: () => void; className: ButtonVariant }] },
  ) => void;
}

export interface NotifierButton {
  text: string;
  action?: () => Promise<void>;
  variant?: ButtonVariant;
}

const install = (app: App) => {
  const notifierDomId = 'notifierPlugin';
  // dom container for mount the NotificationMessage.vue
  let container;
  // like 'app' just for NotificationMessage.vue
  let notifierApp: App;
  // 'props' that NotificationMessage.vue required.
  const baseProps = {
    // define a function to close(unmount) the notifier used for
    // case 1: in NotificationMessage.vue "click notifier appeared and close it"
    // case 2: call 'this.$closeNotification Message()' to close the notifier in anywhere outside NotificationMessage.vue
    close: () => {
      if (notifierApp) {
        notifierApp.unmount();
      }

      container = document.querySelector(`#${notifierDomId}`);

      if (container) {
        document.body.removeChild(container);
      }
    },
  };

  // show Notification Message
  const showMessage = (
    messageBody: string,
    options: {
      type?: PopUpType;
      buttons?: [{ text: string; action: () => void; className: string }];
    },
  ) => {
    const props = { ...baseProps, body: messageBody, ...options };

    // assume the notifier(previous) was not closed, and try to close it.
    props.close();

    // create a dom container and mount th NotificationMessage.vue
    container = document.createElement('div');
    container.setAttribute('id', notifierDomId);
    document.body.appendChild(container);
    notifierApp = createApp(NotificationMessage, props);
    notifierApp.mount(container);
  };

  function success(messageBody: string) {
    showMessage(messageBody, { type: PopUpType.Success });
  }

  function warning(messageBody: string) {
    showMessage(messageBody, { type: PopUpType.Warning });
  }

  function error(
    messageBody: string,
    options?: { buttons: [{ text: string; action: () => void; className: string }] },
  ) {
    showMessage(messageBody, { ...(options && options), type: PopUpType.Error });
  }

  // set 'snotify()' and 'close()' globally
  (app.config.globalProperties.$snotify as Notifier) = { success, error, warning };
  app.config.globalProperties.$closeNotifier = baseProps.close;

  app.provide('$snotify', { success, error, warning });
  app.provide('$closeNotifier', baseProps.close);
};

export default install;
